/**
 * Environment restore: the spec record and the DR rebuild.
 *
 * Two planes that look similar and are not. The *spec* plan compares what is recorded against
 * what is running, and its adopt pass writes records — it never touches Kubernetes. The
 * *rebuild* plan classifies for recovery, and its apply creates databases.
 */

export type SpecBucket =
  | "in_sync"
  | "drifted"
  | "not_recorded"
  | "invalid_record"
  | "no_database"
  | "unknown_backup_account"
  | "no_accounts_row";

export interface ISpecPlanEntry {
  env_id: number;
  bucket: SpecBucket;
  detail?: string | null;
  /** From accounts. An id is what the API needs; a name is what an operator recognises. */
  name?: string | null;
  tenant_name?: string | null;
}

export interface ISpecPlan {
  counts: Record<SpecBucket, number>;
  entries: ISpecPlanEntry[];
  /** 0 means nothing can be rebuilt yet, whatever the other counts say. */
  recorded_count: number;
  adoptable: number;
}

export interface ISpecAdoptResult {
  requested: number[];
  recorded: { env_id: number; was?: string }[];
  failed: { env_id: number; reason?: string }[];
  skipped: { env_id: number; reason?: string }[];
  counts: Record<string, number>;
}

export type RebuildBucket =
  | "healthy"
  | "missing"
  | "partial"
  | "no_backup"
  | "no_record"
  | "invalid_record"
  | "restoring";

export interface IRebuildPlanEntry {
  env_id: number;
  bucket: RebuildBucket;
  detail?: string | null;
  name?: string | null;
  tenant_name?: string | null;
  /** Chosen by the plan and carried into the apply, so a retry restores the same data. */
  backup_set?: string | null;
  /** Set on a clone: its source and the point in time it was restored to (UTC). */
  cloned_from_env_id?: number | null;
  cloned_from_name?: string | null;
  clone_point?: string | null;
  /** A clone of another cluster's environment: "cluster/account/folder" (D17). */
  cloned_from_source?: string | null;
}

export interface IRebuildPlan {
  counts: Record<RebuildBucket, number>;
  entries: IRebuildPlanEntry[];
  recorded_count: number;
  actionable: number;
  max_concurrency: number;
  /** ENVIRONMENT_REBUILD_ENABLED on the server. Off until the drill has been run. */
  enabled: boolean;
}

export interface IRebuildResult {
  requested: number[];
  rebuilt: { env_id: number; backup_set?: string; exec_time?: number; note?: string | null }[];
  failed: { env_id: number; reason?: string }[];
  skipped: { env_id: number; reason?: string }[];
  counts: Record<string, number>;
  /** Asked for and allowed, but over the concurrency cap. These start as slots free up. */
  deferred?: { env_id: number; reason: string }[];
}

export interface IRebuildVerify {
  env_id: number;
  verified: boolean;
  checks: Record<string, boolean>;
  detail?: string | null;
  /** D25: set for a clone given a runtime image. */
  runtime?: {
    requested?: string | null;
    running?: string | null;
    refused?: string | null;
    switched_at?: string | null;
    schema_version?: string | null;
  } | null;
}

/** D25: what is known about a cr-timescaledb tag; poll while `checking`. */
export interface IImageCheck {
  tag: string;
  image: string;
  default: boolean;
  state: "ok" | "failed" | "checking" | null;
  migrations?: number | null;
  last_migration?: string | null;
  detail?: string | null;
}

export const SPEC_BUCKET_LABEL: Record<SpecBucket, string> = {
  in_sync: "Recorded",
  drifted: "Record is stale",
  not_recorded: "Not recorded",
  invalid_record: "Record cannot be rebuilt",
  no_database: "No database",
  unknown_backup_account: "Backup account unknown",
  no_accounts_row: "No accounts record",
};

export const SPEC_BUCKET_HINT: Record<SpecBucket, string> = {
  in_sync: "The recorded spec matches the running database.",
  drifted: "The database was changed after it was recorded. Recording again repairs it.",
  not_recorded: "Running, but nothing is recorded — it could not be rebuilt after a cluster loss.",
  invalid_record:
    "The record exists but a rebuild would refuse it — either it cannot be read back, or " +
    "it holds a value the cluster no longer produces. Both are fixed the same way: record " +
    "it again. Found before a disaster rather than during one.",
  no_database: "Recorded, with nothing running. Rebuilding is what this one needs, not recording.",
  unknown_backup_account:
    "The database backs up to a storage account that no configured option selects, so nothing " +
    "can say where a rebuild should look. Needs the option added, or the account corrected.",
  no_accounts_row:
    "The database is running, but accounts has no live environment for it — either it was " +
    "deleted there while the Kubernetes objects survived, or it was never registered. It " +
    "cannot be recorded or rebuilt until that is resolved.",
};

export const REBUILD_BUCKET_LABEL: Record<RebuildBucket, string> = {
  healthy: "Running",
  missing: "Missing — can be rebuilt",
  partial: "Partly there",
  no_backup: "No backup to restore",
  no_record: "No spec record",
  invalid_record: "Record cannot be rebuilt",
  restoring: "Restoring",
};

export const REBUILD_BUCKET_HINT: Record<RebuildBucket, string> = {
  healthy: "The database is running. Nothing to do.",
  missing:
    "Recorded, absent from the cluster, and a backup exists. This is the only bucket Rebuild " +
    "acts on — it recreates the database under its original id and restores the pinned backup.",
  partial:
    "Something is still there — a leftover volume, a Deployment that is not running, or a " +
    "record that cannot be read. A rebuild would collide with it, so a human decides.",
  no_backup:
    "Recorded, but no backup set was found in the storage account the record names. There is " +
    "nothing to restore from.",
  no_record:
    "Running, with no spec record. It cannot be rebuilt until one exists — record it in the " +
    "Spec records section above.",
  invalid_record:
    "Recorded, absent, and a backup exists — but the record holds a value a rebuild would " +
    "refuse, so Rebuild is not offered. The detail names the field. Fix it on the record or " +
    "on the cluster and re-record; finding this out during a disaster is the point of " +
    "showing it now.",
  restoring:
    "The database is up and pulling its backup. A large one sits here for hours — that is " +
    "progress, not a fault. Use Verify once it reports Running.",
};

/** Where a tenant sits: account > organization > location > tenant (D21). */
export interface IClonePath {
  account_id?: number | null;
  account_name?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  tenant_id?: number | null;
  tenant_name?: string | null;
}

/** An environment a clone may be taken from: any tenant's, deleted ones included (D21). */
export interface ICloneSource extends IClonePath {
  env_id: number;
  uuid?: string | null;
  name?: string | null;
  deleted: boolean;
  /** In the Archive tier: listed, but it cannot be cloned until it is rehydrated. */
  archived?: boolean;
  backup_sets: number;
  oldest_backup?: string | null;
  newest_backup?: string | null;
  last_write_at?: string | null;
  /** Its tenant's primary database. */
  primary?: boolean;
}

/** A tenant a clone may go into, and its primary database if it has one (D22). */
export interface ICloneTarget extends IClonePath {
  tenant_id: number;
  tenant_uuid: string;
  tenant_name: string;
  primary_env_id?: number | null;
  primary_env_name?: string | null;
}

export interface ICloneTargets {
  results: ICloneTarget[];
  total: number;
}

export interface ICloneSources {
  /** ENVIRONMENT_CLONE_ENABLED on the server. */
  enabled: boolean;
  results: ICloneSource[];
  unreadable: { env_id: number; unreadable: string }[];
  /** Accounts rows checked; fewer than `total` means some were not looked at. */
  listed: number;
  total: number;
}

/** The recorded (or live) spec a clone defaults to; storage may not go below it (D10). */
export interface ISourceSpec {
  storage?: string;
  cpu_req?: string;
  memory_req?: string;
  cpus?: string | null;
  memory?: string | null;
  cpu_bkp?: string;
  memory_bkp?: string;
  tier?: string;
  storage_class?: string;
  nodepool?: string;
  resource_allocation?: string;
  rule_engine?: boolean;
  full_backup_schedule?: string;
  incremental_backup_schedule?: string;
  backup_storage_account?: string;
}

/** What a clone of one source can be restored to, all times UTC (D3). */
export interface IRestoreWindow {
  env_id: number;
  earliest: string;
  latest: string;
  latest_basis: "last_write" | "recorded_last_write" | "newest_backup";
  last_write?: string | null;
  newest_backup?: string | null;
  backup_sets: { label: string; stop?: string | null }[];
  live: boolean;
  deleted: boolean;
  source_spec?: ISourceSpec | null;
  source_spec_basis?: "recorded" | "live" | null;
  /** Archived: set times are their starts, read from labels, and a clone is refused until it is rehydrated. */
  archived?: boolean;
  rehydration?: string | null;
  /** `account/folder`: what to rehydrate. */
  repository?: string | null;
  /** Set for another cluster's source (D17); its database size in bytes is the storage floor. */
  cluster?: string | null;
  source_size?: number | null;
  /** Another cluster's WAL archived past the newest backup: Latest takes it, a picked time cannot reach it. */
  archived_until?: string | null;
  /** D25 presets: the cluster's default tag, and the tag a running source on this cluster runs. */
  default_image?: string | null;
  source_image?: string | null;
}

/** Another cluster's environment with backups, by id: its name is in that cluster's accounts (D19). */
export interface IForeignSource {
  env_id: number;
  cluster: string;
  account: string;
  /** The backup folder, e.g. betch-env-10185. */
  name: string;
  archived?: boolean;
  backup_sets: number;
  oldest_backup?: string | null;
  newest_backup?: string | null;
}

export interface IForeignSources {
  enabled: boolean;
  cluster: string;
  results: IForeignSource[];
  unreadable: { env_id: number | null; unreadable: string }[];
  /** Folders read, newest id first; fewer than `total` means some were not looked at. */
  listed: number;
  total: number;
}

/** The clusters whose backups can be cloned here (D18). */
export interface IForeignClusters {
  enabled: boolean;
  clusters: string[];
}

export interface ICloneResult {
  env_id: number;
  source_env_id: number;
  source_cluster?: string | null;
  cross_account?: boolean | null;
  /** D22: whether it became its tenant's primary; null when that could not be told. */
  primary?: boolean | null;
  /** D25: the image it switches to once restored. */
  runtime_image?: string | null;
  point: string;
  exec_time: number;
  /** False when the source's last write could not be confirmed archived. */
  archived_before_clone?: boolean | null;
}
